/**
 * `userRolePermissions.ts` — verbatim port of Unity's
 * `Models/UserRolePermissions.cs`, the pure predicate layer under
 * `IPermissionsModel`.
 *
 * ⚠ ONE ROLE VOCABULARY. Roles here are the Unity INT enums
 * (`SHARED_USER_ROLE` / `ACCOUNT_ROLE` in `sharedLinkDto.ts`) and nothing
 * else. The port previously carried a SECOND, parallel representation — a
 * string union `"viewer"|"commenter"|"editor"|"owner"|"admin"` in
 * `sharedUserRole.ts` — which invented `"admin"` (no Unity counterpart) and
 * DROPPED `NoAccess`, the one value that means "this person's access was
 * revoked". The int enum is the Dynamo row contract, the multiuser wire
 * contract and the admin-pipeline contract, so it wins; the string union is
 * deleted. Do not reintroduce a slug type: the two cannot both be canonical,
 * and a gate that disagrees with the wire about revocation is worse than no
 * gate.
 *
 * These are PURE functions over ints — no IOC, no state, no async. Anything
 * that needs the current user's role, the session's presenter state or the
 * editor mode belongs in `models/permissions/PermissionsModel.ts`, which
 * composes these.
 */

import { ACCOUNT_ROLE, SHARED_USER_ROLE } from "./sharedLinkDto";
import { PRESENTER_TOOLS } from "@models/multiuser/IMultiuserSessionModel";

/**
 * A `SharedUserRole` ordinal. Deliberately a bare `number` rather than a
 * union of the six literals: the value arrives from Dynamo rows, the admin
 * GraphQL pipeline and the multiuser wire, any of which can carry an ordinal
 * a newer desktop build introduced. Every predicate below is written so an
 * UNKNOWN ordinal fails closed (it matches no allow-branch), which is the
 * behaviour a union plus a cast would have thrown away.
 */
export type SharedUserRole = number;

/** An `AccountRole` ordinal (the account LICENCE role — a different enum). */
export type AccountRole = number;

/**
 * `AccountRole` → `SharedUserRole` (`UserRolePermissions.cs:9-19`).
 *
 * Presenter maps to Presenter VERBATIM, matching upstream. The previous web
 * copy collapsed it to Viewer, which is a real divergence: everywhere else
 * in both clients the obsolete Presenter role downgrades to COMMENTER (see
 * `sharedLinkUserRole`), so collapsing it to Viewer here silently stripped
 * comment rights from a Presenter-role account. Downgrading is the READER's
 * job, at the same points Unity does it — not this mapping's.
 */
export function accountRoleToSharedUserRole(accountRole: AccountRole): SharedUserRole {
  switch (accountRole) {
    case ACCOUNT_ROLE.Reader: return SHARED_USER_ROLE.Viewer;
    case ACCOUNT_ROLE.Writer: return SHARED_USER_ROLE.Editor;
    case ACCOUNT_ROLE.Commenter: return SHARED_USER_ROLE.Commenter;
    case ACCOUNT_ROLE.Presenter: return SHARED_USER_ROLE.Presenter;
    default: return SHARED_USER_ROLE.Viewer;
  }
}

/**
 * `SharedUserRole` → `AccountRole` (`UserRolePermissions.cs:21-31`).
 *
 * ⚠ THE TRAP THAT DEFINES THIS WHOLE MODULE: **Owner collapses to Writer**,
 * so an `AccountRole` can NEVER be "Owner". Ownership survives only because
 * `PermissionsModel` re-tests `creatorId === userId` BEFORE consulting the
 * account role (`PermissionsModel.cs:100-105`). Any code that round-trips a
 * role through this function and back reports an owner as an Editor —
 * including Unity's own presence payload (`MultiuserSessionModel.cs:625`),
 * which is why the web deliberately matches that rather than "fixing" it.
 */
export function sharedUserRoleToAccountRole(role: SharedUserRole): AccountRole {
  switch (role) {
    case SHARED_USER_ROLE.Owner: return ACCOUNT_ROLE.Writer;
    case SHARED_USER_ROLE.Editor: return ACCOUNT_ROLE.Writer;
    case SHARED_USER_ROLE.Commenter: return ACCOUNT_ROLE.Commenter;
    case SHARED_USER_ROLE.Presenter: return ACCOUNT_ROLE.Presenter;
    default: return ACCOUNT_ROLE.Reader;
  }
}

/**
 * `UserRolePermissions.CanUseTool` (`:39-59`) — verbatim, INCLUDING the fact
 * that it is not one rule.
 *
 * ⚠ THE TWO ARMS ARE DISJOINT AND IGNORE EACH OTHER'S INPUT:
 *   - Annotation / Sketch / BoundingBox test the ROLE (Owner|Editor|Commenter)
 *     and ignore `isPresenter` entirely.
 *   - FlashLight / Explosion / Slice / RotateMove / Scale / Grab test
 *     `isPresenter` and ignore the ROLE entirely.
 *   - Everything else — None, Laser, Teleportation, Repos, and any ordinal a
 *     newer build adds — falls through to `true` and is ungated.
 *
 * So a Viewer holding presentership gets Slice and Grab, and an Owner who is
 * not presenting does not. Collapsing this into a single severity ladder is
 * the single most likely mis-port in the permissions area; the signature
 * takes both inputs precisely because a reader would otherwise assume both
 * always apply.
 */
export function canUseTool(
  role: SharedUserRole,
  tool: number,
  isPresenter: boolean,
): boolean {
  switch (tool) {
    case PRESENTER_TOOLS.AnnotationTool:
    case PRESENTER_TOOLS.SketchTool:
    case PRESENTER_TOOLS.BoundingBoxTool:
      return role === SHARED_USER_ROLE.Owner
        || role === SHARED_USER_ROLE.Editor
        || role === SHARED_USER_ROLE.Commenter;

    case PRESENTER_TOOLS.FlashLightTool:
    case PRESENTER_TOOLS.ExplosionTool:
    case PRESENTER_TOOLS.SliceTool:
    case PRESENTER_TOOLS.RotateMoveTool:
    case PRESENTER_TOOLS.ScaleTool:
    case PRESENTER_TOOLS.GrabTool:
      return isPresenter;

    default:
      return true;
  }
}

/**
 * `CanAuthorProject` (`:64-67`) — the ROLE half only: Owner or Editor.
 *
 * ⚠ This is NOT `IPermissionsModel.canAuthorProject()`, which additionally
 * requires the user to be the PRESENTER. The two differ by exactly one
 * conjunct and upstream uses both, three lines apart
 * (`RightPanelViewModel.cs:573` vs `:579`). Porting the model's predicate as
 * this function alone reproduces roughly none of the desktop gating.
 */
export function canAuthorProject(role: SharedUserRole): boolean {
  return role === SHARED_USER_ROLE.Owner || role === SHARED_USER_ROLE.Editor;
}

/**
 * `CanShareProject` (`:69-72`) — Owner or Editor.
 *
 * The one predicate on `IPermissionsModel` with NO presenter term
 * (`PermissionsModel.cs:341-344`): sharing a project is not a presenting act.
 */
export function canShareProject(role: SharedUserRole): boolean {
  return role === SHARED_USER_ROLE.Owner || role === SHARED_USER_ROLE.Editor;
}

/** `CanModifyProject` (`:96-99`) — delete and rename. Owner ONLY. */
export function canModifyProject(role: SharedUserRole): boolean {
  return role === SHARED_USER_ROLE.Owner;
}

/**
 * `IsProjectOwner` (`:79-82`) — `CreatorId == UserID`.
 *
 * MUST NOT default true on missing data. A cross-account shared project
 * arrives from the admin share pipeline with NO `creatorId`, so a permissive
 * default would hand a Viewer owner rights over someone else's project — the
 * exact mistake `useProjectList` and `useSharePanel` each carry a comment
 * about. Both sides are checked for emptiness, so `undefined === undefined`
 * can never read as ownership.
 */
export function isProjectOwner(
  project: { readonly creatorId?: string | null } | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!project || !userId) return false;
  const creatorId = project.creatorId;
  if (!creatorId) return false;
  return creatorId === userId;
}

/**
 * `CanAssignPresenter` (`:34-37`) — BOTH participants must be presenter-
 * eligible. Note it says nothing about roles: presentership is an
 * admin-portal capability, not a share role.
 */
export function canAssignPresenter(
  me: { readonly canBePresenter: boolean } | null | undefined,
  other: { readonly canBePresenter: boolean } | null | undefined,
): boolean {
  return me?.canBePresenter === true && other?.canBePresenter === true;
}

/**
 * `CanMoveProject` (`:88-91`) — a core licence, a user-created project, and
 * the project living in the caller's own account.
 */
export function canMoveProject(
  project: {
    readonly isUserCreated?: boolean;
    readonly customerId?: string | null;
  } | null | undefined,
  user: { readonly customerId: string | null; readonly hasCoreLicense: boolean } | null | undefined,
): boolean {
  if (!project || !user) return false;
  if (!user.hasCoreLicense) return false;
  if (project.isUserCreated === false) return false;
  return (project.customerId ?? user.customerId) === user.customerId;
}

/** `CanEditFolder` (`:93-96`) — bare ownership of the folder. */
export function canEditFolder(
  folder: { readonly creatorId?: string | null } | null | undefined,
  userId: string | null | undefined,
): boolean {
  return isProjectOwner(folder, userId);
}

/** `CanMoveToFolder` (`:97-100`) — the folder is in the caller's account. */
export function canMoveToFolder(
  folder: { readonly customerId?: string | null } | null | undefined,
  customerId: string | null | undefined,
): boolean {
  if (!folder || !customerId) return false;
  return folder.customerId === customerId;
}

/**
 * Human label for a role — Unity renders the enum's `ToString()`. Used in the
 * AI refusal copy (`AIGuardrailsModel.Permission.cs:33-34` prints the role by
 * name) and the share sheet.
 */
export function sharedUserRoleLabel(role: SharedUserRole): string {
  switch (role) {
    case SHARED_USER_ROLE.Owner: return "Owner";
    case SHARED_USER_ROLE.Editor: return "Editor";
    case SHARED_USER_ROLE.Commenter: return "Commenter";
    case SHARED_USER_ROLE.Viewer: return "Viewer";
    case SHARED_USER_ROLE.NoAccess: return "No access";
    case SHARED_USER_ROLE.Presenter: return "Commenter";
    default: return "Viewer";
  }
}
